import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import { Autocomplete } from "@react-google-maps/api";
import { parseAddress, formatPhoneNumber } from "../../utils/Utils.js";
import PlanSelection from "../../components/PlanSelection.jsx";
import { manageAPI } from "../../api/index.js";
import { useGoogleMaps } from "@/utils/googleMaps.jsx";

function AccountSetup() {
  const user = useSelector((state) => state.app.user);
  const dispatch = useDispatch();

  const [legalBusinessName, setLegalBusinessName] = useState("");
  const [billingSameAsUser, setBillingSameAsUser] = useState(true);
  const [billingEmail, setBillingEmail] = useState(user?.email || "");
  const [selectedPlan, setSelectedPlan] = useState("basic");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [stores, setStores] = useState([{ name: "", address: "", phone: "", parsedAddress: null }]);
  const autoCompleteRefs = useRef({});
  const storeRefs = useRef({});

  const { isLoaded } = useGoogleMaps();

  // Initialize first store with user address when user data is available
  useEffect(() => {
    if (user?.address) {
      setStores((prevStores) => {
        // Only initialize if first store is empty and user address exists
        if (prevStores.length > 0 && !prevStores[0].parsedAddress) {
          const addressString = `${user.address || ""} ${user.city || ""} ${user.state || ""} ${user.zip || ""}`.trim();
          const formattedPhone = user.phone ? formatPhoneNumber(user.phone) || user.phone : "";
          return [{
            name: "",
            address: addressString,
            phone: formattedPhone,
            parsedAddress: user.location ? {
              address: user.address,
              city: user.city,
              state: user.state,
              zip: user.zip,
              location: user.location
            } : null
          }];
        }
        return prevStores;
      });
    }
  }, [user]);

  useEffect(() => {
    if (user?.email && billingSameAsUser) {
      setBillingEmail(user.email);
    }
  }, [user?.email, billingSameAsUser]);

  // Progress / Steps
  const [currentStep, setCurrentStep] = useState(1);

  const canContinueStep1 = useMemo(() => {
    return Boolean(legalBusinessName && billingEmail);
  }, [legalBusinessName, billingEmail]);

  const canContinueStep2 = useMemo(() => {
    if (stores.length === 0) return false;
    // Validate that ALL stores have required fields filled out
    // Store Name, Store Address (with parsedAddress), and Store Phone are all required
    return stores.every((store) => {
      return Boolean(
        store.name && 
        store.name.trim() && 
        store.address && 
        store.address.trim() && 
        store.parsedAddress &&
        store.phone &&
        store.phone.trim()
      );
    });
  }, [stores]);

  const canContinueStep3 = useMemo(() => {
    return Boolean(selectedPlan);
  }, [selectedPlan]);

  const updateStore = (idx, key, value) => {
    setStores((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };

  const handlePlaceChanged = (idx) => {
    const autocomplete = autoCompleteRefs.current[idx];
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place) {
        const parsed = parseAddress(place);
        const addressString = `${parsed.address || ""} ${parsed.city || ""} ${parsed.state || ""} ${parsed.zip || ""}`.trim();
        updateStore(idx, "address", addressString);
        updateStore(idx, "parsedAddress", parsed);
      }
    }
  };

  const handleAddonToggle = (addonId, isChecked) => {
    if (isChecked) {
      setSelectedAddons([...selectedAddons, addonId]);
    } else {
      setSelectedAddons(selectedAddons.filter(id => id !== addonId));
    }
  };

  const addStore = () => {
    const newIndex = stores.length;
    setStores((prev) => [...prev, { name: "", address: "", phone: "", parsedAddress: null }]);
    // Scroll to the new store section after it's rendered
    setTimeout(() => {
      const newStoreElement = storeRefs.current[newIndex];
      if (newStoreElement) {
        newStoreElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };
  const removeStore = (idx) => setStores((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (currentStep === 1) {
      if (canContinueStep1) setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      if (canContinueStep2) setCurrentStep(3);
      return;
    }
    if (currentStep === 3) {
      if (!canContinueStep3) return;

      // Step 3: Submit account setup data
      setLoading(true);
      try {
        // Format stores data for API
        const storesData = stores.map((store) => ({
          name: store.name,
          address: store.parsedAddress?.address || "",
          city: store.parsedAddress?.city || "",
          state: store.parsedAddress?.state || "",
          zip: store.parsedAddress?.zip || "",
          location: store.parsedAddress?.location || null,
          phone: store.phone.replace(/\D/g, ""), // Send phone as digits only
        }));

        // Filter out SEO Boost if Growth plan is selected (it's included)
        const addonsToSend = selectedPlan === "growth" 
          ? selectedAddons.filter(addon => addon !== "seo-boost")
          : selectedAddons;

        const response = await manageAPI.post("prospect/signup/step3", {
          legal_business_name: legalBusinessName,
          billing_email: billingEmail,
          stores: storesData,
          subscription_plan: selectedPlan,
          addons: addonsToSend,
        });

        // Get response data
        const responseData = response.data;

        // Check if job_id is present and set prospect_conversion_id
        if (responseData?.data?.job_id) {
          dispatch({ type: "SET_PROSPECT_CONVERSION_ID", payload: responseData.data.job_id });
        }        
      } catch (e) {
        let errorMessage = "An error occurred. Please try again.";
        try {
          if (e.response) {
            const errorData = e.response.data;
            errorMessage = errorData?.message || errorData?.error || errorMessage;
          } else if (e.message) {
            errorMessage = e.message;
          }
        } catch {
          errorMessage = e.message || errorMessage;
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Account Setup</h1>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Step {currentStep} of 3
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {currentStep === 1 ? "Merchant Info" : currentStep === 2 ? "Basic Store Info" : "Select Plan"}
          </p>
        </div>
        <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 rounded-full ${
              currentStep >= 3
                ? "bg-theme-button-primary w-full"
                : currentStep === 2
                ? "bg-theme-button-primary w-2/3"
                : currentStep === 1
                ? "bg-theme-button-primary w-1/3"
                : "bg-slate-200 dark:bg-slate-700 w-0"
            }`}
          ></div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Step 1: Merchant Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                Let's start with your business information. This will be used for billing and account management purposes.
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-white text-slate-800" htmlFor="legal_name">
                  Legal Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="legal_name"
                  className="form-input w-full"
                  type="text"
                  placeholder="Your legal business entity name"
                  value={legalBusinessName}
                  onChange={(e) => setLegalBusinessName(e.target.value)}
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium mb-1 dark:text-white text-slate-800" htmlFor="billing_email">
                    Billing Email <span className="text-red-500">*</span>
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      className="form-checkbox mr-2"
                      checked={billingSameAsUser}
                      onChange={(e) => setBillingSameAsUser(e.target.checked)}
                    />
                    Same as your email
                  </label>
                </div>
                <input
                  id="billing_email"
                  className="form-input w-full"
                  type="email"
                  placeholder="billing@yourcompany.com"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  disabled={billingSameAsUser}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Stores List */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                Now let's add your store locations. You can add multiple stores if you have them. You can also add more locations later.
              </p>
            </div>
            {stores.map((store, idx) => (
            <div key={idx} ref={(el) => { storeRefs.current[idx] = el; }} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Store {idx + 1}</h3>
                {stores.length > 1 && (
                  <SecondaryButton type="button" onClick={() => removeStore(idx)}>
                    Remove
                  </SecondaryButton>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white text-slate-800" htmlFor={`store_name_${idx}`}>
                    Store Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={`store_name_${idx}`}
                    className="form-input w-full"
                    type="text"
                    placeholder="Store name"
                    value={store.name}
                    onChange={(e) => updateStore(idx, "name", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white text-slate-800" htmlFor={`store_address_${idx}`}>
                    Store Address <span className="text-red-500">*</span>
                  </label>
                  {isLoaded ? (
                    <Autocomplete
                      onLoad={(autocomplete) => {
                        autoCompleteRefs.current[idx] = autocomplete;
                      }}
                      onPlaceChanged={() => handlePlaceChanged(idx)}
                      options={{
                        types: ["address"],
                        componentRestrictions: { country: "us" },
                      }}
                    >
                      <input
                        id={`store_address_${idx}`}
                        className="form-input w-full"
                        type="text"
                        placeholder="123 Main St, City, ST 90210"
                        value={store.address}
                        onChange={(e) => updateStore(idx, "address", e.target.value)}
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      id={`store_address_${idx}`}
                      className="form-input w-full"
                      type="text"
                      placeholder="Loading address autocomplete..."
                      disabled
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white text-slate-800" htmlFor={`store_phone_${idx}`}>
                    Store Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={`store_phone_${idx}`}
                    className="form-input w-full"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={store.phone}
                    onChange={(e) => {
                      const value = e.target.value;
                      const formatted = formatPhoneNumber(value);
                      if (formatted) {
                        updateStore(idx, "phone", formatted);
                      } else {
                        const cleaned = value.replace(/\D/g, "");
                        if (cleaned.length <= 10) {
                          updateStore(idx, "phone", value);
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          ))}

          <div>
            <SecondaryButton type="button" onClick={addStore}>
              + Add Additional Store
            </SecondaryButton>
          </div>
        </div>
        )}

        {/* Step 3: Plan Selection */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                Choose the plan that best fits your business needs. You can always upgrade or change your plan later.
              </p>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-6">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                  No charges until your stores go live
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  You won't be charged until your stores are activated and ready to accept orders. Set up your account worry-free!
                </p>
              </div>
            </div>
            <PlanSelection
              selectedPlan={selectedPlan}
              onPlanSelect={setSelectedPlan}
              storeCount={stores.length}
              mode="select"
              showAddons={true}
              selectedAddons={selectedAddons}
              onAddonToggle={handleAddonToggle}
            />
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}

        <div className="pt-2 flex items-center gap-3">
          {currentStep > 1 && (
            <SecondaryButton type="button" onClick={() => setCurrentStep(currentStep - 1)}>
              Back
            </SecondaryButton>
          )}
          <PrimaryButton 
            type="submit" 
            loading={loading}
            disabled={
              currentStep === 1 ? !canContinueStep1 : 
              currentStep === 2 ? !canContinueStep2 : 
              !canContinueStep3 || loading
            }
          >
            {currentStep === 1 ? "Continue" : currentStep === 2 ? "Continue" : "Finish"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}

export default AccountSetup;


